/* file description */
import useMetaStore from '@/store/useMetaStore';
import type {
  RefListType,
  RelationTreeType,
} from '@/asset/AssetTask/templates/Cascade/type';
import { TransformValuesType, TransformValuesRecordType } from '../type';
import {
  createAssetCascadeData,
  getApproverSettingInfo,
  getBaseRecordList,
} from '@/api/cascade';
import { message } from 'antd';
import useValuesStore from '@/store/useValuesStore';
import { checkModelValues, getJsonAta } from '@/utils';
import { RuleTigger } from '@/const';
import { asyncTimeout } from '@/utils/timerPromise';
// 深度遍历
// function treeForeach(tree, func) {
//   tree.forEach((data) => {
//     func(data);
//     data.children && treeForeach(data.children, func); // 遍历子树
//   });
// }
// 广度遍历
function treeForeach(tree: RelationTreeType[], func: Function) {
  let node;
  const list = [...tree];
  // eslint-disable-next-line no-cond-assign
  while ((node = list.shift())) {
    func(node);
    node.children && list.push(...node.children);
  }
}
// 找到节点
function treeFind(tree: any[], func: Function): TransformValuesType | null {
  for (const data of tree) {
    if (func(data)) return data;
    if (data.record) {
      const res = treeFind(data.record, func);
      if (res) return res;
    }
    if (data.children) {
      const res = treeFind(data.children, func);
      if (res) return res;
    }
  }
  return null;
}

const evetsEum = {
  add: 'create',
  edit: 'updata',
  view: 'view',
};

interface useSaveEventProps {
  refList: RefListType;
}

export const useSaveEvent = ({ refList }: useSaveEventProps) => {
  const sotreState = useMetaStore();
  const { values, getValues, realoadValue, pageRules } = useValuesStore();
  const {
    taskInfo,
    moduleList,
    pageConfig,
    pageType,
    taskId,
    utils,
    fsToken,
  } = sotreState;
  const urlVersion = utils?.queryParams?.version;
  const urlOpenVersion =
    utils?.queryParams?.openVersion === 'true' ? true : undefined;

  const getApproverSettingData = async (type?: string) => {
    const promiseList = (Array.from(refList.current) as any[])?.map(
      (itemRef) => {
        return itemRef[1].validateFields();
      },
    );
    try {
      await Promise.all(promiseList);
    } catch (e) {
      console.error(e);
      message.error('请填写必选项');
      return;
    }
    const approverSettingRes = await getApproverSettingInfo({ taskId });
    const nodeRes = await getBaseRecordList({
      appId: 'tech',
      modelId: 'approval_design',
      data: {
        condition: 'relation_task_id = ${relation_task_id}',
        parameters: { relation_task_id: taskId },
      },
    });
    const nodeList = nodeRes?.data?.[0]?.schema_values?.nodes || [];
    const approverSettingData = approverSettingRes?.data || {};
    approverSettingData.needAssignNodes?.forEach((item: any) => {
      const nodeItem = nodeList.find((el: any) => el.id === item.assigned_node);
      if (nodeItem) {
        item.nodeName = nodeItem.data?.basicConfiguration?.name;
        item.assigneeFilterRole =
          nodeItem.data?.basicConfiguration?.assigneeFilterRole;
        item.ccFilterRole = nodeItem.data?.basicConfiguration?.ccFilterRole;
      }
    });
    return approverSettingData;
  };

  const validateForm = async (type?: string) => {
    const promiseList = (Array.from(refList.current) as any[])?.map(
      (itemRef) => {
        return itemRef[1].validateFields();
      },
    );
    try {
      await Promise.all(promiseList);
    } catch (e) {
      console.error(e);
      message.error('请填写必选项');
      return false;
    }
    const transformValue = getSaveRequestBody(type);

    // 配置保存前校验
    // const resultCheckConfig = exclusiveConfig?.dataCheckList?.find((item: any) => item?.triggerType === 'saveBefore');
    // if (resultCheckConfig) {
    //   const errCheckStack = checkModelValues(values, (taskInfo?.maxTag >= 3), resultCheckConfig.checkConfig);
    //   if (errCheckStack.length > 0) {
    //     const errMsg = errCheckStack.map((item) => item.message).join(' and ');
    //     message.error(resultCheckConfig?.name || errMsg);
    //     return;
    //   }
    // }

    // 基于规则进行校验
    const submitRules =
      pageRules?.filter((rule) => rule.trigger === RuleTigger.Submit) || [];
    if (submitRules.length > 0) {
      let passCount = 0;
      for (const rule of submitRules) {
        let res;
        try {
          res = getJsonAta(rule.RULES).evaluate(values);
        } catch (e) {
          console.warn(e);
        }
        if (res) {
          passCount += 1;
        } else {
          message.error(rule.message);
          break;
        }
      }
      if (passCount < submitRules.length) {
        return false;
      }
    }

    return transformValue;
  };

  const pureSave = async (type?: string, approverSetData?: string) => {
    await asyncTimeout({ ms: 550 });

    const transformValue = await validateForm(type);
    if (transformValue === false) {
      return;
    }

    console.log('==========', {
      transformValue,
      taskId,
      pageType,
      urlOpenVersion,
      urlVersion,
      approverSetData,
      fsToken,
    });

    const res = await createAssetCascadeData(
      transformValue,
      taskId,
      pageType,
      urlOpenVersion,
      urlVersion,
      approverSetData,
      fsToken,
    );
    return !!res?.success;
  };

  const save = async (type?: string, approverSetData?: string) => {
    await asyncTimeout({ ms: 550 });

    const transformValue = await validateForm(type);
    if (transformValue === false) {
      return;
    }

    const res = await createAssetCascadeData(
      transformValue,
      taskId,
      pageType,
      urlOpenVersion,
      urlVersion,
      approverSetData,
      fsToken,
    );
    if (res.success) {
      message.success('保存成功');
      // 刷新列表页的数据
      utils?.qiankun?.qiankun?.refresh &&
        utils.qiankun.qiankun.refresh(utils.queryParams.tabFrom);
      utils?.qiankun?.refresh &&
        utils.qiankun.refresh(utils.queryParams.tabFrom);
      utils?.saveSuccess && utils?.saveSuccess(res, transformValue);
      if (utils?.queryParams?.SHOWTYPE === 'modal') {
        realoadValue();
        return;
      }
      if (pageType === 'add') {
        const version = res?.data?.version ?? '';
        const urlParams = urlOpenVersion
          ? `?version=${urlVersion || version}&openVersion=${urlOpenVersion}`
          : '';
        const recordId = res?.data?.recordId;
        utils?.buiNavigate({
          type: 'spa',
          path: `/lcp/task/${taskId}/detail/${recordId}${urlParams}`,
        });
      } else {
        const version = res?.data?.record?.version ?? '';
        const urlParams = urlOpenVersion
          ? `?version=${urlVersion || version}&openVersion=${urlOpenVersion}`
          : '';
        utils?.buiNavigate({
          type: 'spa',
          path: `/lcp/task/${taskId}/detail/${res.data.record?.headId}${urlParams}`,
        });
      }
      return;
    }
    utils?.saveFail && utils?.saveFail(res);
  };

  const getSaveRequestBody = (type = '') => {
    const system_event =
      type === 'submit-btns'
        ? `${taskId}#submit`
        : `${taskId}#${evetsEum[pageType]}`;
    const dataSource = getValues();
    let transformValue: TransformValuesType = {} as TransformValuesType;
    treeForeach(taskInfo.relationTree, (info: RelationTreeType) => {
      const { code, attrCode, parentCode, parentAttrCode } = info;
      const nameSpaceCode = moduleList.find((module) => module.handle === code)
        ?.namespaceHandle;
      // 生成根节点
      if (parentCode == null) {
        transformValue = {
          appId: nameSpaceCode!,
          modelId: code,
          record: [
            {
              children: [],
              values: { ...dataSource[code], system_event },
            },
          ],
          refHeadField: parentAttrCode,
          refName: attrCode,
        };
        return;
      }
      // 找到带有moduleId的节点
      const targetNode = treeFind(
        [transformValue],
        (data: TransformValuesType) => data.modelId === parentCode,
      );
      if (!targetNode) return;
      targetNode.record.forEach((record) => {
        let resultValues = [];
        switch (info.floor) {
          case 2: {
            resultValues =
              dataSource?.[code]?.map((valueData) => ({
                _id: valueData?._id,
                children: [],
                values: Object.assign(valueData, {
                  toJSON: null,
                  RECORD: null,
                }),
              })) || [];
            break;
          }
          case 3: {
            resultValues =
              dataSource?.[code]
                ?.filter((item) => item._headId === record._id)
                ?.map((valueData) => ({
                  _id: valueData?._id,
                  children: [],
                  values: Object.assign(valueData, {
                    toJSON: null,
                    RECORD: null,
                  }),
                })) || [];
            break;
          }
        }
        record.children.push({
          appId: nameSpaceCode!,
          modelId: code,
          record: resultValues,
          refHeadField: parentAttrCode,
          refName: attrCode,
        });
      });
    });

    return transformValue;
  };

  return {
    pureSave,
    save,
    validateForm,
    getSaveRequestBody,
    getApproverSettingData,
  };
};
